import type { CSSProperties } from 'react';

function normalize(value: number, max: number) {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
  return Math.min(100, Math.max(0, (value / max) * 100));
}

export type ProgressBarProps = {
  value: number;
  max?: number;
  label?: string;
  className?: string;
};

export function ProgressBar({ value, max = 100, label, className }: ProgressBarProps) {
  const percent = normalize(value, max);
  const fillStyle = { '--hopper-progress': `${percent}%` } as CSSProperties;

  return (
    <div className={['hopper-progress-bar', className].filter(Boolean).join(' ')}>
      {label && <span className="hopper-progress-bar__label">{label}</span>}
      <div
        className="hopper-progress-bar__track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={Math.min(max, Math.max(0, value))}
      >
        <span className="hopper-progress-bar__fill" style={fillStyle} />
      </div>
    </div>
  );
}

export type ProgressRingProps = {
  value: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  className?: string;
};

export function ProgressRing({ value, max = 100, size = 56, strokeWidth = 6, label, className }: ProgressRingProps) {
  const percent = normalize(value, max);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - percent / 100);

  return (
    <span
      className={['hopper-progress-ring', className].filter(Boolean).join(' ')}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(max, Math.max(0, value))}
      style={{ width: size, height: size }}
    >
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
        <circle className="hopper-progress-ring__track" cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={strokeWidth} />
        <circle
          className="hopper-progress-ring__value"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      {label && <span className="hopper-progress-ring__label">{label}</span>}
    </span>
  );
}
